import React, { useEffect, useState } from 'react';
import { StickyNote, Trash2 } from 'lucide-react';
import { fmtDateTime } from '../lib/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;

/** Private notes — visible only to the logged-in admin/staff member.
 *  Permanent until the owner deletes them. */
export default function PersonalNotes() {
  const [notes, setNotes] = useState([]);
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await fetch(`${API}/api/me/notes`, { credentials: 'include' });
      if (res.ok) setNotes((await res.json()).notes || []);
    } catch { /* offline */ }
  };

  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!body.trim() || busy) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/me/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ body: body.trim() }),
      });
      if (res.ok) { setBody(''); await load(); }
    } finally {
      setBusy(false);
    }
  };

  const remove = async (noteId) => {
    if (!window.confirm('Delete this note permanently?')) return;
    const res = await fetch(`${API}/api/me/notes/${noteId}`, { method: 'DELETE', credentials: 'include' });
    if (res.ok) setNotes((n) => n.filter((x) => x.note_id !== noteId));
  };

  return (
    <div className="max-w-3xl space-y-5" data-testid="personal-notes">
      <div className="bg-white rounded-2xl border border-tge-navy/5 p-5">
        <div className="flex items-center gap-2 mb-3">
          <StickyNote size={16} className="text-tge-gold" />
          <h3 className="font-serif-display text-lg text-tge-navy">New note</h3>
          <span className="text-[11px] text-tge-muted ml-auto">Private — only you can see and delete these.</span>
        </div>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={3}
          placeholder="Write yourself a note — reminders, follow-ups, anything…"
          className="w-full border border-tge-navy/15 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-tge-navy/40 resize-none"
          data-testid="personal-note-input"
        />
        <div className="flex justify-end mt-2">
          <button
            onClick={add}
            disabled={busy || !body.trim()}
            className="text-xs font-semibold bg-tge-navy hover:bg-tge-navy/90 disabled:opacity-40 text-white rounded-full px-5 py-2 transition"
            data-testid="personal-note-save"
          >
            {busy ? 'Saving…' : 'Save note'}
          </button>
        </div>
      </div>

      {notes.length === 0 ? (
        <div className="text-center text-sm text-tge-muted py-8">No notes yet.</div>
      ) : (
        <div className="space-y-2.5">
          {notes.map((n) => (
            <div key={n.note_id} className="bg-white border border-tge-navy/10 rounded-xl px-4 py-3 flex gap-3" data-testid={`personal-note-${n.note_id}`}>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-tge-navy whitespace-pre-wrap break-words">{n.body}</div>
                <div className="text-[10px] text-tge-navy/40 mt-1">{fmtDateTime(n.created_at)}</div>
              </div>
              <button
                onClick={() => remove(n.note_id)}
                className="shrink-0 self-start text-rose-400 hover:text-rose-600 transition"
                title="Delete note"
                data-testid={`personal-note-delete-${n.note_id}`}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
